import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays the day-supply for the selected product variant
 * (e.g. "30 Days" for a 1-pack, "90 Days" for a 3-pack).
 * Listens for variant select events and updates the value using a variant-id -> day-count
 * map rendered server-side into a data attribute, so it stays correct if pack pricing
 * or naming ever changes.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} supplyValue - The element that displays the day count
 *
 * @extends {Component<Refs>}
 */
class ProductSupplyDays extends Component {
  requiredRefs = ['supplyValue'];

  connectedCallback() {
    super.connectedCallback();
    this.supplyMap = this.#parseSupplyMap();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #parseSupplyMap() {
    try {
      return JSON.parse(this.dataset.supplyMap || '{}');
    } catch (error) {
      console.warn('[product-supply] Failed to parse supply map:', error);
      return {};
    }
  }

  /**
   * Handles product select event and updates the displayed day-supply.
   * @param {CustomEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        if (resource?.id && this.supplyMap[resource.id] != null) {
          this.refs.supplyValue.textContent = `${this.supplyMap[resource.id]} Days`;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-supply] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-supply-days')) {
  customElements.define('product-supply-days', ProductSupplyDays);
}
